import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { DragDropContext } from 'react-beautiful-dnd';
import isEqual from 'lodash/isEqual';

import { Button, ModalV2 as Modal } from '@utilities';
import { readInstantSurvey, updateQuestionTopBottomOptions } from '@api/instant_survey';

import { buildInitialLists, findSurveyQuestion, move, reorder } from './utilities/helpers';
import Top2BoxColumn from './components/Top2BoxColumn';
import styles from './_index.module.scss';

const ModalTop2Box = ({ isActive, onApplied, onClose, question, surveyId }) => {
  const [bottomItems, setBottomItems] = useState([]);
  const [initialBottomIds, setInitialBottomIds] = useState([]);
  const [initialTopIds, setInitialTopIds] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isSurveyLoading, setIsSurveyLoading] = useState(false);
  const [surveyQuestion, setSurveyQuestion] = useState(null);
  const [poolItems, setPoolItems] = useState([]);
  const [topItems, setTopItems] = useState([]);

  const controllerRef = useRef(null);

  const originalOrderMap = useMemo(() => {
    const pairs = surveyQuestion?.answers?.map((ans, idx) => [String(ans.id), idx]) ?? [];
    return new Map(pairs);
  }, [surveyQuestion?.answers]);

  const sortByOriginal = useCallback(
    (items = []) => {
      if (!items.length || originalOrderMap.size === 0) return items.slice();
      return items.slice().sort((a, b) => {
        const ai = originalOrderMap.get(String(a.id));
        const bi = originalOrderMap.get(String(b.id));
        return (ai ?? Number.POSITIVE_INFINITY) - (bi ?? Number.POSITIVE_INFINITY);
      });
    },
    [originalOrderMap]
  );

  useEffect(() => {
    if (!isActive || !surveyId) return;

    const controller = new AbortController();

    const fetchSurveyDetails = async () => {
      try {
        setIsSurveyLoading(true);
        const surveyResponse = await readInstantSurvey({
          id: surveyId,
          params: { include_response_count: true },
          signal: controller.signal,
        });

        setSurveyQuestion(findSurveyQuestion(surveyResponse?.questions, question?.id));
      } catch (error) {
        if (error?.name === 'CanceledError' || error?.name === 'AbortError') return;
        console.error('Failed to fetch survey details:', error);
        setSurveyQuestion(null);
      } finally {
        setIsSurveyLoading(false);
      }
    };

    fetchSurveyDetails();
    return () => controller.abort();
  }, [isActive, surveyId, question?.id]);

  useEffect(() => {
    if (!isActive || !surveyQuestion) return;

    const { bottom, pool, top } = buildInitialLists(surveyQuestion);

    setTopItems(top);
    setBottomItems(bottom);
    setPoolItems(sortByOriginal(pool));
    setInitialTopIds(top.map(({ id }) => id));
    setInitialBottomIds(bottom.map(({ id }) => id));
  }, [isActive, surveyQuestion, sortByOriginal]);

  const listsById = useMemo(
    () => ({ bottom: bottomItems, pool: poolItems, top: topItems }),
    [bottomItems, poolItems, topItems]
  );

  const setListById = useCallback((listId, nextItems) => {
    if (listId === 'bottom') setBottomItems(nextItems);
    else if (listId === 'pool') setPoolItems(nextItems);
    else setTopItems(nextItems);
  }, []);

  const insertIntoPool = useCallback(
    (items) => {
      if (!items?.length) return;
      setPoolItems((prev) => {
        const seen = new Set(prev.map((i) => i?.id));
        const merged = [...prev];
        for (const item of items) if (!seen.has(item?.id)) merged.push(item);
        return sortByOriginal(merged);
      });
    },
    [sortByOriginal]
  );

  const removeFromList = useCallback(
    (listId, itemId) => {
      const list = listsById[listId];
      const removeIndex = list.findIndex((i) => i.id === itemId);
      if (removeIndex === -1) return;

      const next = list.slice();
      const [removed] = next.splice(removeIndex, 1);

      setListById(listId, next);
      insertIntoPool([removed]);
    },
    [listsById, insertIntoPool, setListById]
  );

  const clearList = useCallback(
    (listId) => {
      const list = listsById[listId];
      if (!list?.length) return;
      setListById(listId, []);
      insertIntoPool(list);
    },
    [listsById, insertIntoPool, setListById]
  );

  const onDragEnd = useCallback(
    ({ destination, source }) => {
      if (!destination) return;
      if (source.droppableId === destination.droppableId && source.index === destination.index) {
        return;
      }

      if (source.droppableId === 'pool' && destination.droppableId === 'pool') {
        setPoolItems((prev) => sortByOriginal(prev));
        return;
      }

      if (source.droppableId === destination.droppableId) {
        const reordered = reorder(listsById[source.droppableId], source.index, destination.index);
        setListById(source.droppableId, reordered);
        return;
      }

      const appendIndex = listsById[destination.droppableId].length;
      const adjustedDestination = { ...destination, index: appendIndex };

      const moved = move(
        listsById[source.droppableId],
        listsById[destination.droppableId],
        source,
        adjustedDestination
      );

      if (source.droppableId === 'pool') {
        setPoolItems(sortByOriginal(moved.pool));
      } else {
        setListById(source.droppableId, moved[source.droppableId]);
      }

      if (destination.droppableId === 'pool') {
        setPoolItems(sortByOriginal(moved.pool));
      } else {
        setListById(destination.droppableId, moved[destination.droppableId]);
      }
    },
    [listsById, setListById, sortByOriginal]
  );

  const hasChanges = useMemo(() => {
    const topIds = topItems.map(({ id }) => id);
    const bottomIds = bottomItems.map(({ id }) => id);
    return !(isEqual(topIds, initialTopIds) && isEqual(bottomIds, initialBottomIds));
  }, [topItems, bottomItems, initialTopIds, initialBottomIds]);

  const handleApply = useCallback(async () => {
    if (!surveyQuestion?.id || !surveyId) return;

    const controller = new AbortController();
    controllerRef.current = controller;

    const payload = {
      bottomNOptions: bottomItems.map(({ id }) => Number(id)),
      questionId: surveyQuestion.id,
      signal: controller.signal,
      surveyId,
      topNOptions: topItems.map(({ id }) => Number(id)),
    };

    try {
      setIsSaving(true);
      await updateQuestionTopBottomOptions(payload);
      onApplied?.();
      onClose();
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.name === 'AbortError') return;
      console.error('Failed to update Top 2 Box settings:', error);
    } finally {
      setIsSaving(false);
    }
  }, [bottomItems, topItems, onClose, onApplied, surveyQuestion?.id, surveyId]);

  return (
    <Modal
      buttons={[
        <Button key="cancel-btn" onClick={onClose} text="Cancel" variant="secondary" />,
        <Button
          disabled={isSaving || isSurveyLoading || !hasChanges}
          key="submit-btn"
          onClick={handleApply}
          text="Apply Changes"
        />,
      ]}
      isActive={isActive}
      isScrollable
      onClose={onClose}
      size="large"
      title="Top 2 Box"
    >
      <div className={styles['modal-top-2-box']}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Top2BoxColumn
            id="top"
            isLoading={isSurveyLoading}
            items={topItems}
            onClear={() => clearList('top')}
            onRemoveItem={(item) => removeFromList('top', item.id)}
            placeholder="Drag answer options to combine them for the top box."
            title="Top"
          />

          <Top2BoxColumn
            id="bottom"
            isLoading={isSurveyLoading}
            items={bottomItems}
            onClear={() => clearList('bottom')}
            onRemoveItem={(item) => removeFromList('bottom', item.id)}
            placeholder="Drag answer options to combine them for the bottom box."
            title="Bottom"
          />

          <Top2BoxColumn
            id="pool"
            isLoading={isSurveyLoading}
            items={poolItems}
            placeholder="All answer options have
been combined."
          />
        </DragDropContext>
      </div>
    </Modal>
  );
};

ModalTop2Box.propTypes = {
  isActive: PropTypes.bool.isRequired,
  onApplied: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  question: PropTypes.shape({
    answers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        text: PropTypes.string,
      })
    ),
    bottomNOptions: PropTypes.arrayOf(PropTypes.number),
    id: PropTypes.number,
    text: PropTypes.string,
    topNOptions: PropTypes.arrayOf(PropTypes.number),
  }),
  surveyId: PropTypes.number,
};

export default ModalTop2Box;
